import { useLayout } from '@hooks/useLayout';
import { DefaultRoute, Routes } from './routes';
import ErrorComponent from '../views/misc/Error';
import BlankLayout from '@src/layouts/BlankLayout';
import NotAuthorized from '../views/misc/NotAuthorized';
import VerticalLayout from '@src/layouts/VerticalLayout';
import { AbilityContext } from '@src/utility/context/Can';
import LayoutWrapper from '@layouts/components/layout-wrapper';
import { useRouterTransition } from '@hooks/useRouterTransition';
import { Fragment, Suspense, useContext, useEffect } from 'react';
import {
  Route,
  Switch,
  Redirect,
  BrowserRouter as AppRouter,
  useLocation,
} from 'react-router-dom';
import {
  useIsLoggedIn,
  useLoginByToken,
  useIsInitialized,
  useNeedPhoneConfirmation,
  useLogout,
  useProfile,
} from '../store/Auth/authHooks';
import Footer from '../components/overwritten-core/layouts/components/footer';
import { checkIsGuardValidation } from '../utility/validators/role-validtion';

const Router = () => {
  const isLoggedIn = useIsLoggedIn();
  const isNeedPhoneConfirmation = useNeedPhoneConfirmation();
  const loginByToken = useLoginByToken();
  const isInitialized = useIsInitialized();
  const logout = useLogout();

  // Change to default direction on app start
  useEffect(() => {
    // ** Get HTML Tag
    const isRtl = localStorage.getItem('i18nextLng') === 'he';
    const element = document.getElementsByTagName('html')[0];

    // ** If isRTL then add attr dir='rtl' with HTML else attr dir='ltr'
    if (isRtl) {
      element.setAttribute('dir', 'rtl');
    } else {
      element.setAttribute('dir', 'ltr');
    }
  }, []);

  useEffect(() => {
    if (!isLoggedIn && !isInitialized) {
      loginByToken()
        .catch(() => {
          console.log('Failed to login');
        })
        .finally(() => {});
    }
  }, [loginByToken, isLoggedIn]);

  // ** Hooks
  const { layout, setLayout, setLastLayout } = useLayout();
  const { transition, setTransition } = useRouterTransition();
  // ** ACL Ability Context
  const ability = useContext(AbilityContext);

  // ** Default Layout
  const DefaultLayout = 'VerticalLayout';

  // ** All of the available layouts
  const Layouts = { BlankLayout, VerticalLayout };

  // ** Current Active Item
  const currentActiveItem = null;

  // ** Return Filtered Array of Routes & Paths
  const LayoutRoutesAndPaths = (layout) => {
    const LayoutRoutes = [];
    const LayoutPaths = [];

    if (Routes) {
      Routes.filter((route) => {
        // ** Checks if Route layout or Default layout matches current layout
        if (
          route.layout === layout ||
          (route.layout === undefined && DefaultLayout === layout)
        ) {
          LayoutRoutes.push(route);
          LayoutPaths.push(route.path);
        }
      });
    }

    return { LayoutRoutes, LayoutPaths };
  };

  /**
   ** Final Route Component Checks for ResetPassword & User Role and then redirects to the route
   */
  const FinalRoute = (props) => {
    const profile = useProfile();

    const route = props.route;
    let action, resource;
    // ** Assign vars based on route meta
    if (route.meta) {
      action = route.meta.action ? route.meta.action : null;
      resource = route.meta.resource ? route.meta.resource : null;
    }
    if (isNeedPhoneConfirmation && !route.meta?.confirmPhone && isLoggedIn) {
      return <Redirect to="/confirm-phone" />;
    } else if (
      (!isNeedPhoneConfirmation && !isLoggedIn && route.meta === undefined) ||
      (!isLoggedIn &&
        !isNeedPhoneConfirmation &&
        route.meta &&
        !route.meta.authRoute &&
        !route.meta.publicRoute)
    ) {
      /**
       ** If user is not Logged in & route meta is undefined
       ** OR
       ** If user is not Logged in & route.meta.authRoute, !route.meta.publicRoute are undefined
       ** Then redirect user to login
       */
      return <Redirect to="/login" />;
    } else if (
      route.meta &&
      route.meta.authRoute &&
      isLoggedIn &&
      !isNeedPhoneConfirmation
    ) {
      // ** If route has meta and authRole and user is Logged in then redirect user to home page (DefaultRoute)
      // if (profile.role === 'guard') {
      //   return <Redirect to="/all-guests" />;
      // }
      return <Redirect to="/" />;

      // else if (
      //       (checkIsGuardValidation(profile) && !route.meta?.guardRoute) ||
      //       (route.profileFunctionValidation &&
      //           !route.profileFunctionValidation(profile))
      //   ) {
      //     logout();
      //     return <Redirect to="/login" />;
    } else {
      // ** If none of the above render component
      return (
        <div className="layout-global">
          <route.component {...props} />
          <Footer />
        </div>
      );
    }
  };

  // ** Return Route to Render
  const ResolveRoutes = () => {
    return Object.keys(Layouts).map((layout, index) => {
      // ** Convert Layout parameter to Layout Component
      // ? Note: make sure to keep layout and component name equal
      const LayoutTag = Layouts[layout];

      // ** Get Routes and Paths of the Layout
      const { LayoutRoutes, LayoutPaths } = LayoutRoutesAndPaths(layout);
      // ** We have freedom to display different layout for different route
      // ** We have made LayoutTag dynamic based on layout, we can also replace it with the only layout component,
      // ** that we want to implement like VerticalLayout or HorizontalLayout
      // ** We segregated all the routes based on the layouts and Resolved all those routes inside layouts

      // ** RouterProps to pass them to Layouts
      const routerProps = {};
      return (
        <Route path={LayoutPaths} key={index}>
          <LayoutTag
            layout={layout}
            setLayout={setLayout}
            transition={transition}
            routerProps={routerProps}
            setLastLayout={setLastLayout}
            setTransition={setTransition}
            currentActiveItem={currentActiveItem}
          >
            <Switch>
              {LayoutRoutes.map((route) => {
                return (
                  <Route
                    key={route.path}
                    path={route.path}
                    exact={route.exact === true}
                    render={(props) => {
                      // ** Assign props to routerProps
                      Object.assign(routerProps, {
                        ...props,
                        meta: route.meta,
                      });

                      return (
                        <Fragment>
                          {/* Layout Wrapper to add classes based on route's layout, appLayout and className */}

                          {route.layout === 'BlankLayout' ? (
                            <Fragment>
                              <FinalRoute route={route} {...props} />
                            </Fragment>
                          ) : (
                            <LayoutWrapper
                              layout={DefaultLayout}
                              transition={transition}
                              setTransition={setTransition}
                              /* Conditional props */
                              /*eslint-disable */
                              {...(route.appLayout
                                ? {
                                    appLayout: route.appLayout,
                                  }
                                : {})}
                              {...(route.meta
                                ? {
                                    routeMeta: route.meta,
                                  }
                                : {})}
                              {...(route.className
                                ? {
                                    wrapperClass: route.className,
                                  }
                                : {})}
                              /*eslint-enable */
                            >
                              <Suspense fallback={null}>
                                <FinalRoute route={route} {...props} />
                              </Suspense>
                            </LayoutWrapper>
                          )}
                        </Fragment>
                      );
                    }}
                  />
                );
              })}
            </Switch>
          </LayoutTag>
        </Route>
      );
    });
  };

  return (
    <AppRouter basename={process.env.REACT_APP_BASENAME}>
      {isInitialized ? (
        <Switch>
          {/* If user is logged in Redirect user to DefaultRoute else to login */}
          <Route
            exact
            path="/"
            render={() => {
              return isLoggedIn ? (
                // profile.role === 'guard' ? (
                //   <Redirect to={'all-guests'} />
                // ) :
                <Redirect to={DefaultRoute} />
              ) : (
                <Redirect to="/login" />
              );
            }}
          />
          {/* Not Auth Route */}
          <Route
            exact
            path="/misc/not-authorized"
            render={() => (
              <Layouts.BlankLayout>
                <NotAuthorized />
              </Layouts.BlankLayout>
            )}
          />
          {ResolveRoutes()}

          {/* NotFound Error page */}
          <Route path="*" component={ErrorComponent} />
        </Switch>
      ) : null}
    </AppRouter>
  );
};

export default Router;
